import React, { useCallback, useMemo } from 'react';
import type { PickerProps } from 'rc-picker';
import Picker from 'rc-picker';
import jaJP from 'rc-picker/lib/locale/ja_JP';
import momentConfig from 'rc-picker/lib/generate/moment';
import type { InputProps } from '@this/shared/ui/inputs/input';
import { Input } from '@this/shared/ui/inputs/input';
import { IconButton, useFormControl } from '@material-ui/core';
import type { Moment } from 'moment';
import type { PickerPanelDateProps } from 'rc-picker/lib/PickerPanel';
import moment from 'moment';
import { Close } from '@material-ui/icons';

import { disabledDate, usePopupStyle } from '@this/shared/ui/inputs/datepicker/datepicker_base/datepicker.utils';
import { DatepickerPanelWrapper } from '@this/shared/ui/inputs/datepicker/datepicker_base/datepicker_base';
import { useStyles } from './datepicker.styles';

type DateType = Moment;

export const DATEPICKER_VARIANT = {
  outlined: 'outlined',
  ghost: 'ghost'
} as const;
export type DatepickerVariant = keyof typeof DATEPICKER_VARIANT;

/**
 * 内部的にはmomentを使用するが、in/outはjsの標準クラスであるDateにする。
 * 日付ライブラリを変更する際のするときの影響範囲を少なくするため
 */
export type DatepickerProps = {
  type?: 'date' | 'datetime-local';
  picker?: 'date' | 'month' | 'year';
  min?: Date;
  max?: Date;
  value?: Date;
  defaultValue?: Date;
  dateFormat?: string;
  variant?: DatepickerVariant;
  fullWidth?: boolean;
  disabledClear?: boolean;
  dataTestIdPrefix?: string;
  onChange?: (value: Date | null) => void;
} & Pick<InputProps, 'size' | 'error'> &
  Pick<PickerProps<DateType>, 'className' | 'style' | 'placeholder' | 'disabled'>;

const DEFAULT_DATE_FORMAT = 'YYYY/MM/DD';
const DEFAULT_TIME_FORMAT = 'HH:mm';

export const Datepicker = ({
  className,
  type = 'date',
  picker = 'date',
  min,
  max,
  value: rawValue,
  defaultValue: rawDefaultValue,
  disabled: rawDisabled,
  onChange,
  size,
  error,
  dateFormat,
  variant = 'outlined',
  fullWidth,
  disabledClear,
  dataTestIdPrefix,
  placeholder: rawPlaceholder,
  ...props
}: DatepickerProps) => {
  const muiFormControl = useFormControl();
  const styles = useStyles({ size, fullWidth: fullWidth ?? muiFormControl?.fullWidth, variant });
  const disabled = muiFormControl?.disabled ? muiFormControl.disabled : rawDisabled;

  const handleOnChange = useCallback(
    (date: DateType | null) => {
      if (!date) {
        onChange?.(null);
        return;
      }
      const next = type === 'datetime-local' ? date.clone().second(0).millisecond(0) : date;
      onChange?.(next.toDate());
    },
    [onChange, type]
  );

  const format = useMemo(() => {
    if (dateFormat) return dateFormat;
    if (picker === 'month') return 'YYYY/MM';
    if (picker === 'year') return 'YYYY';
    return type === 'datetime-local' ? `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT}` : DEFAULT_DATE_FORMAT;
  }, [type, dateFormat, picker]);

  const placeholder = useMemo(() => {
    if (rawPlaceholder) return rawPlaceholder;
    if (picker === 'month') return '年/月';
    if (picker === 'year') return '年';
    return dateFormat ?? '年/月/日';
  }, [rawPlaceholder, dateFormat, picker]);
  const showTime = useMemo<PickerPanelDateProps<DateType>['showTime']>(
    () => (type === 'datetime-local' ? { showSecond: false, format: DEFAULT_TIME_FORMAT } : false),
    [type]
  );
  const valueTime = rawValue?.getTime();
  const defaultValueTime = rawDefaultValue?.getTime();
  const minTime = min?.getTime();
  const maxTime = max?.getTime();
  const value = useMemo<DateType | undefined>(() => {
    if (valueTime == null || Number.isNaN(valueTime)) return undefined;
    const next = moment(valueTime);
    if (type === 'datetime-local') {
      next.second(0).millisecond(0);
    }
    return next;
  }, [valueTime, type]);
  const defaultValue = useMemo<DateType | undefined>(() => {
    if (defaultValueTime == null || Number.isNaN(defaultValueTime)) return undefined;
    const next = moment(defaultValueTime);
    if (type === 'datetime-local') {
      next.second(0).millisecond(0);
    }
    return next;
  }, [defaultValueTime, type]);
  const handleDisabledDate = useCallback(
    (date: DateType) =>
      disabledDate(date, {
        min: minTime != null && !Number.isNaN(minTime) ? new Date(minTime) : undefined,
        max: maxTime != null && !Number.isNaN(maxTime) ? new Date(maxTime) : undefined,
        type
      }),
    [minTime, maxTime, type]
  );
  const popupStyle = usePopupStyle();

  return (
    <Picker<Moment>
      className={`${className} ${styles.root}`}
      picker={picker}
      showToday={picker === 'date'}
      allowClear={!disabledClear}
      value={value}
      defaultValue={defaultValue}
      placeholder={placeholder}
      locale={jaJP}
      generateConfig={momentConfig}
      inputRender={inputProps => (
        <InputAsDatepicker
          {...inputProps}
          size={size}
          error={error}
          data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-input` : undefined}
        />
      )}
      clearIcon={<ClearButton />}
      panelRender={originPanel => (
        <DatepickerPanelWrapper
          panel={originPanel}
          dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-popup` : undefined}
        />
      )}
      showTime={showTime}
      format={format}
      popupStyle={popupStyle}
      disabledDate={handleDisabledDate}
      disabled={disabled}
      onChange={handleOnChange}
      {...props}
    />
  );
};

const InputAsDatepicker = React.memo(
  React.forwardRef<
    HTMLInputElement,
    Pick<DatepickerProps, 'size' | 'error'> & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>
  >((props, ref) => {
    return <Input {...props} ref={ref} readOnly fullWidth />;
  })
);

const ClearButton = React.memo(() => (
  <IconButton size="small">
    <Close fontSize="small" />
  </IconButton>
));
